import { useState, useEffect } from "react";
import { useParams } from "react-router";
import {
  getAuth,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";

import { Grid, Typography } from "@mui/material";

import { LoadingButton } from "@aclymatepackages/atoms";

import Link from "../atoms/mui/Link";

import AccountAccessLayout from "../layouts/AccountAccessLayout";
import PasswordInput from "./PasswordInput";

const INVALID_CODE_MESSAGE =
  "This password reset link is invalid or has expired.";

const resetErrorMessage = ({ code }) =>
  code === "auth/weak-password"
    ? "That password is too weak. Please choose a longer passphrase."
    : INVALID_CODE_MESSAGE;

const RequestNewLinkText = ({ message }) => (
  <Typography variant="body1" color="error" align="center">
    {message} You can{" "}
    <Link href="/forgot-password" color="inherit" underline="always">
      request a new password reset email.
    </Link>
  </Typography>
);

// The code in the URL is Firebase's single-use, expiring oobCode -- it, not the
// account's uid, is what authorizes the password change, and the Firebase
// client SDK redeems it directly so no endpoint of ours can be tricked into
// setting a password for an arbitrary account.
const PasswordReset = () => {
  const { oobCode } = useParams();
  const [password, setPassword] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [codeStatus, setCodeStatus] = useState("checking");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const checkResetCode = async () => {
      const isValidCode = await verifyPasswordResetCode(getAuth(), oobCode)
        .then(() => true)
        .catch(() => false);

      return setCodeStatus(isValidCode ? "valid" : "invalid");
    };

    checkResetCode();
  }, [oobCode]);

  const onPasswordReset = async () => {
    setIsLoading(true);
    setError("");

    const resetError = await confirmPasswordReset(
      getAuth(),
      oobCode,
      password
    ).then(
      () => null,
      (e) => resetErrorMessage(e)
    );

    setIsLoading(false);

    if (resetError) {
      return setError(resetError);
    }

    return setPasswordChanged(true);
  };

  const layoutProps = {
    formTitle: "Reset your password",
    formSubtitle:
      "We recommend a memorable passphrase. Try combining multiple words that are meaningful to you like marketingpicklestaplerhorse.",
    layoutType: "login",
  };

  if (passwordChanged) {
    return (
      <AccountAccessLayout {...layoutProps}>
        <Grid item>
          <Typography variant="subtitle1">
            Your password has been reset. You can now use use your new password
            to{" "}
            <Link href="/" color="inherit" underline="always">
              login in to Aclymate.
            </Link>
          </Typography>
        </Grid>
      </AccountAccessLayout>
    );
  }

  if (codeStatus === "invalid") {
    return (
      <AccountAccessLayout {...layoutProps}>
        <Grid item>
          <RequestNewLinkText message={INVALID_CODE_MESSAGE} />
        </Grid>
      </AccountAccessLayout>
    );
  }

  return (
    <AccountAccessLayout {...layoutProps}>
      <Grid item>
        <PasswordInput
          password={password}
          setPassword={setPassword}
          helperText
        />
      </Grid>
      <Grid item container justifyContent="center">
        <Grid item>
          <LoadingButton
            variant="contained"
            color="primary"
            disabled={password.length < 12 || codeStatus !== "valid"}
            onClick={onPasswordReset}
            isLoading={isLoading}
            label="Reset Password"
          />
        </Grid>
      </Grid>
      {error && (
        <Grid item>
          <RequestNewLinkText message={error} />
        </Grid>
      )}
    </AccountAccessLayout>
  );
};
export default PasswordReset;
